import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useI18n } from '../i18n';
import { RequireAdmin } from '../components/RequireAdmin';
import { useAuth } from '../lib/auth';
import {
  listAdminComments,
  setCommentReviewed,
  deleteAdminComment,
  getAdminSettings,
  putAdminSettings,
  type AdminComment,
  type AppSettings,
} from '../lib/admin';

// Page size for the queue refill. The page UX is "walk one at a time", so the
// list backs a small in-memory buffer rather than feeding a paginated table.
const BUFFER_SIZE = 25;

// Human-friendly labels for the heuristic reasons returned by the backend.
const REASON_LABEL: Record<string, string> = {
  url: 'link',
  profanity: 'profanity',
  burst: 'burst (≥3 / 60s)',
  duplicate: 'duplicate',
};

export function AdminComments() {
  const { localePath } = useI18n();
  const { accessToken } = useAuth();

  const [buffer, setBuffer] = useState<AdminComment[]>([]);
  const [total, setTotal] = useState(0); // unreviewed count on the server
  const [seen, setSeen] = useState(0); // how many we've actioned this session
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false); // mutation in flight
  const [err, setErr] = useState<string | null>(null);
  const [flaggedOnly, setFlaggedOnly] = useState(false);

  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [settingsDraft, setSettingsDraft] = useState<{ enabled: boolean; message: string } | null>(null);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [settingsErr, setSettingsErr] = useState<string | null>(null);

  const current = buffer[0];

  // Refill the buffer when it empties (or the filter changes). The backend
  // already orders flagged → not, so we just take the head.
  async function refill() {
    if (!accessToken) return;
    setLoading(true);
    setErr(null);
    try {
      const data = await listAdminComments(accessToken, {
        reviewed: false,
        flaggedOnly,
        limit: BUFFER_SIZE,
        offset: 0,
      });
      setBuffer(data.comments);
      setTotal(data.total);
    } catch (e: any) {
      setErr(e?.message || `${e}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, flaggedOnly]);

  // Initial settings load.
  useEffect(() => {
    if (!accessToken) return;
    getAdminSettings(accessToken)
      .then((s) => {
        setSettings(s);
        setSettingsDraft({
          enabled: s.comments.enabled,
          message: s.comments.disabledMessage ?? '',
        });
      })
      .catch((e) => setSettingsErr(e?.message || `${e}`));
  }, [accessToken]);

  // Advance: drop the head; if we're down to a small tail, refill in the
  // background so the admin never sees a "loading" spinner mid-stream.
  function advance(removedTotal = true) {
    setBuffer((b) => b.slice(1));
    setSeen((n) => n + 1);
    if (removedTotal) setTotal((n) => Math.max(0, n - 1));
    if (buffer.length <= 3) {
      // fire-and-forget refill — refill() itself sets loading state
      refill();
    }
  }

  async function approve() {
    if (!current || !accessToken || busy) return;
    setBusy(true);
    setErr(null);
    try {
      await setCommentReviewed(accessToken, current.id, true);
      advance(true);
    } catch (e: any) {
      setErr(e?.message || `${e}`);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!current || !accessToken || busy) return;
    if (!confirm(`Delete this comment? "${truncate(current.body, 80)}"`)) return;
    setBusy(true);
    setErr(null);
    try {
      await deleteAdminComment(accessToken, current.id);
      advance(true);
    } catch (e: any) {
      setErr(e?.message || `${e}`);
    } finally {
      setBusy(false);
    }
  }

  function skip() {
    if (!current || busy) return;
    // Skip moves on without changing server state. Buffer might re-surface
    // the row on next refill — that's intentional, the admin can leave
    // ambiguous calls for later.
    advance(false);
  }

  // Keyboard shortcuts. Live only while an actionable comment is on screen.
  const currentIdRef = useRef<number | null>(null);
  currentIdRef.current = current?.id ?? null;
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (currentIdRef.current == null || busy) return;
      if (e.key === 'a' || e.key === 'A') {
        e.preventDefault();
        approve();
      } else if (e.key === 'd' || e.key === 'D') {
        e.preventDefault();
        remove();
      } else if (e.key === 's' || e.key === 'S') {
        e.preventDefault();
        skip();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy]);

  // ── Settings save ─────────────────────────────────────────────────────
  const settingsDirty = useMemo(() => {
    if (!settings || !settingsDraft) return false;
    return (
      settingsDraft.enabled !== settings.comments.enabled ||
      (settingsDraft.message ?? '') !== (settings.comments.disabledMessage ?? '')
    );
  }, [settings, settingsDraft]);

  async function saveSettings() {
    if (!accessToken || !settingsDraft || settingsBusy) return;
    setSettingsBusy(true);
    setSettingsErr(null);
    try {
      const next = await putAdminSettings(accessToken, {
        comments: {
          enabled: settingsDraft.enabled,
          disabledMessage: settingsDraft.message,
        },
      });
      setSettings(next);
      setSettingsDraft({
        enabled: next.comments.enabled,
        message: next.comments.disabledMessage ?? '',
      });
    } catch (e: any) {
      setSettingsErr(e?.message || `${e}`);
    } finally {
      setSettingsBusy(false);
    }
  }

  return (
    <RequireAdmin>
      <div class="admin-page">
        <div class="admin-shell">
          <header class="admin-header">
            <a class="admin-back" href={localePath('/admin')}>← Admin</a>
            <h1>Comments</h1>
            <div class="admin-who" style={{ marginLeft: 'auto' }}>
              <span class="admin-mono">{total} unreviewed</span>
            </div>
          </header>

          <p class="admin-lede">
            Post-moderation queue for per-match chat. Auto-flagged comments
            (links, profanity, burst posting) bubble to the top.
            <br />
            <strong>Shortcuts:</strong> <kbd>A</kbd> approve, <kbd>D</kbd> delete, <kbd>S</kbd> skip.
          </p>

          {/* ── Settings strip ── */}
          {settingsDraft && (
            <section class="admin-settings-strip" style={stripStyle}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={settingsDraft.enabled}
                  onChange={(e) =>
                    setSettingsDraft({
                      ...settingsDraft,
                      enabled: (e.target as HTMLInputElement).checked,
                    })
                  }
                />
                <span>Comments enabled</span>
              </label>
              <input
                class="admin-filter-input"
                type="text"
                placeholder="Maintenance message (shown when disabled)"
                value={settingsDraft.message}
                onInput={(e) =>
                  setSettingsDraft({
                    ...settingsDraft,
                    message: (e.target as HTMLInputElement).value,
                  })
                }
                style={{ flex: 1, minWidth: 240 }}
              />
              <button
                class="admin-link"
                onClick={saveSettings}
                disabled={!settingsDirty || settingsBusy}
              >
                {settingsBusy ? 'Saving…' : 'Save'}
              </button>
              {settingsErr && <span class="admin-error">{settingsErr}</span>}
            </section>
          )}

          {/* ── Filter ── */}
          <div class="admin-chip-row" style={{ margin: '8px 0 16px' }}>
            <button
              type="button"
              class={`admin-chip ${!flaggedOnly ? 'on' : ''}`}
              onClick={() => setFlaggedOnly(false)}
            >
              All unreviewed
            </button>
            <button
              type="button"
              class={`admin-chip ${flaggedOnly ? 'on' : ''}`}
              onClick={() => setFlaggedOnly(true)}
            >
              Flagged only
            </button>
          </div>

          {err && <p class="admin-error">{err}</p>}

          {/* ── Current comment card ── */}
          {loading && !current && <p class="admin-lede">Loading queue…</p>}

          {!loading && !current && (
            <div style={emptyStyle}>
              <h2 style={{ margin: '0 0 6px' }}>Queue empty</h2>
              <p class="admin-lede" style={{ margin: 0 }}>
                Nothing to moderate{flaggedOnly ? ' in the flagged bucket' : ''}.
                {seen > 0 && <> You actioned <strong>{seen}</strong> this session.</>}
              </p>
            </div>
          )}

          {current && (
            <article style={cardStyle}>
              <header style={cardHeaderStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Avatar comment={current} />
                  <div>
                    <div style={{ fontWeight: 600 }}>
                      {current.nickname || <em style={{ opacity: 0.5 }}>no nickname</em>}
                    </div>
                    <div class="admin-mono" style={{ fontSize: 11, opacity: 0.6 }}>
                      {current.userId.slice(0, 8)}…
                    </div>
                  </div>
                  {current.canton && <CantonChip code={current.canton} />}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div class="admin-mono" style={{ fontSize: 12 }}>
                    Match #{current.matchNumber}
                  </div>
                  <div class="admin-mono" style={{ fontSize: 11, opacity: 0.6 }}>
                    {fmtDate(current.createdAt)}
                  </div>
                </div>
              </header>

              {current.flagged && current.flagReasons && current.flagReasons.length > 0 && (
                <div style={flagStripStyle}>
                  <span class="admin-mono" style={{ fontSize: 11, opacity: 0.7 }}>flagged:</span>
                  {current.flagReasons.map((r) => (
                    <span key={r} style={reasonChipStyle}>{REASON_LABEL[r] ?? r}</span>
                  ))}
                </div>
              )}

              <blockquote style={bodyStyle}>{current.body}</blockquote>

              <div style={actionsStyle}>
                <button
                  class="admin-link"
                  style={approveBtnStyle}
                  onClick={approve}
                  disabled={busy}
                >
                  ✓ Approve <kbd style={kbdStyle}>A</kbd>
                </button>
                <button
                  class="admin-link"
                  style={deleteBtnStyle}
                  onClick={remove}
                  disabled={busy}
                >
                  ✕ Delete <kbd style={kbdStyle}>D</kbd>
                </button>
                <button class="admin-link" onClick={skip} disabled={busy}>
                  Skip <kbd style={kbdStyle}>S</kbd>
                </button>
                <span class="admin-mono" style={{ marginLeft: 'auto', opacity: 0.6, fontSize: 12 }}>
                  {seen > 0 && <>actioned <strong>{seen}</strong> · </>}
                  {Math.max(0, total - 1)} left after this
                </span>
              </div>
            </article>
          )}
        </div>
      </div>
    </RequireAdmin>
  );
}

function Avatar({ comment }: { comment: AdminComment }) {
  if (comment.avatarUrl) {
    return (
      <img
        src={comment.avatarUrl}
        alt=""
        width={40}
        height={40}
        style={{ borderRadius: '50%', objectFit: 'cover' }}
      />
    );
  }
  const letter = (comment.nickname?.[0] || '?').toUpperCase();
  return (
    <div
      aria-hidden
      style={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        background: 'rgba(245, 237, 224, 0.08)',
        display: 'grid',
        placeItems: 'center',
        fontWeight: 600,
      }}
    >
      {letter}
    </div>
  );
}

function CantonChip({ code }: { code: string }) {
  const c = code.toLowerCase();
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 10px',
        borderRadius: 9999,
        background: 'rgba(245, 237, 224, 0.06)',
        border: '1px solid rgba(245, 237, 224, 0.1)',
        fontFamily: 'IBM Plex Mono, Menlo, monospace',
        fontSize: 12,
        letterSpacing: '0.05em',
      }}
    >
      <img
        src={`/cantons/${c}.svg`}
        alt=""
        width={16}
        height={16}
        style={{ display: 'block' }}
        onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = 'none')}
      />
      {code.toUpperCase()}
    </span>
  );
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : s.slice(0, n - 1) + '…';
}

function fmtDate(iso: string): string {
  const m = iso.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  return m ? `${m[1]} ${m[2]}` : iso;
}

// ── Inline styles. Kept local to avoid bloating admin.css for a single page;
//    the rest of the admin pages do the same trick. ──

const stripStyle: Record<string, string | number> = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
  alignItems: 'center',
  padding: '10px 14px',
  border: '1px solid rgba(245, 237, 224, 0.1)',
  borderRadius: 8,
  background: 'rgba(245, 237, 224, 0.03)',
  margin: '0 0 12px',
};

const cardStyle: Record<string, string | number> = {
  border: '1px solid rgba(245, 237, 224, 0.12)',
  borderRadius: 12,
  background: 'rgba(245, 237, 224, 0.03)',
  padding: '18px 20px',
};

const cardHeaderStyle: Record<string, string | number> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  marginBottom: 12,
};

const flagStripStyle: Record<string, string | number> = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  flexWrap: 'wrap',
  padding: '6px 10px',
  borderRadius: 6,
  background: 'rgba(232, 123, 30, 0.08)',
  border: '1px solid rgba(232, 123, 30, 0.3)',
  margin: '4px 0 12px',
};

const reasonChipStyle: Record<string, string | number> = {
  fontFamily: 'IBM Plex Mono, Menlo, monospace',
  fontSize: 11,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: '#f0a64e',
  padding: '2px 8px',
  borderRadius: 9999,
  border: '1px solid rgba(232, 123, 30, 0.4)',
};

const bodyStyle: Record<string, string | number> = {
  margin: '4px 0 16px',
  padding: '14px 16px',
  background: 'rgba(0, 0, 0, 0.2)',
  borderLeft: '3px solid rgba(245, 237, 224, 0.2)',
  borderRadius: 4,
  fontSize: 15,
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

const actionsStyle: Record<string, string | number> = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
};

const approveBtnStyle: Record<string, string | number> = {
  background: 'rgba(64, 160, 80, 0.15)',
  border: '1px solid rgba(64, 160, 80, 0.5)',
  color: '#7fd095',
  padding: '8px 14px',
  borderRadius: 6,
};

const deleteBtnStyle: Record<string, string | number> = {
  background: 'rgba(200, 60, 60, 0.12)',
  border: '1px solid rgba(200, 60, 60, 0.5)',
  color: '#e88080',
  padding: '8px 14px',
  borderRadius: 6,
};

const kbdStyle: Record<string, string | number> = {
  fontFamily: 'IBM Plex Mono, Menlo, monospace',
  fontSize: 10,
  padding: '1px 5px',
  marginLeft: 4,
  border: '1px solid currentColor',
  borderRadius: 3,
  opacity: 0.6,
};

const emptyStyle: Record<string, string | number> = {
  padding: '32px 20px',
  textAlign: 'center',
  border: '1px dashed rgba(245, 237, 224, 0.15)',
  borderRadius: 12,
  background: 'rgba(245, 237, 224, 0.02)',
};
